// Product analytics via PostHog. Mirrors the Sentry posture in main.tsx: no-ops entirely
// when VITE_POSTHOG_KEY is empty, never sends PII (we identify by the opaque Clerk user id
// and never pass names/emails/amounts as properties), and cookieless (localStorage only).
// Session replay and autocapture are off — pageviews are captured manually on route change
// (see AnalyticsBootstrap), everything else is an explicit event.
//
// posthog-js (~90KB gzip) is loaded in its own async chunk so it never sits in the main
// bundle — and isn't fetched at all when there's no key.

type PostHog = typeof import('posthog-js').default

// PostHog US cloud. Override with VITE_POSTHOG_HOST if the project ever moves regions.
const DEFAULT_HOST = 'https://us.i.posthog.com'

let client: PostHog | null = null

export async function initAnalytics(
  key: string | undefined = import.meta.env.VITE_POSTHOG_KEY,
  host: string = import.meta.env.VITE_POSTHOG_HOST || DEFAULT_HOST
): Promise<void> {
  if (client || !key) return
  const { default: posthog } = await import('posthog-js')
  posthog.init(key, {
    api_host: host,
    persistence: 'localStorage',
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    respect_dnt: false,
  })
  client = posthog
  // The landing pageview — AnalyticsBootstrap's effect may have already run and been dropped.
  posthog.capture('$pageview')
}

export function isAnalyticsEnabled(): boolean {
  return client !== null
}

type EventProps = Record<string, string | number | boolean | null | undefined>

export function track(event: string, props?: EventProps): void {
  client?.capture(event, props)
}

export function capturePageview(): void {
  client?.capture('$pageview')
}

/** distinctId is the Clerk user id — opaque, stable, and what the backend maps users from. */
export function identifyUser(distinctId: string): void {
  client?.identify(distinctId)
}

export function setHouseholdGroup(householdId: string): void {
  client?.group('household', householdId)
}

/** Called on sign-out — unlinks subsequent anonymous events from the previous user. */
export function resetAnalytics(): void {
  client?.reset()
}
